import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../services/store';
import { ClassroomState, Student } from '../types/classroom';

const initialState: ClassroomState = {
  type: 'student',
  student: {
    countStudent: 0,
    maxStudent: 0,
    totalCnt: 0,
    addCnt: 0,
    list: [],
    page: 0,
    keyword: '',
    groupList: [],
  },
  group: {
    totalCnt: 0,
    list: [],
    page: 0,
    keyword: '',
  },
  teacher: {
    totalCnt: 0,
    list: [],
    page: 0,
    keyword: '',
  },
};

export const classroomSlice = createSlice({
  name: 'student',
  initialState,
  reducers: {
    classInfoTypeChanged: (state, { payload }) => {
      state.type = payload;
    },
    studentCountChanged: (state, { payload }) => {
      state.student.countStudent = payload;
    },
    studentMaxStudentChanged: (state, { payload }) => {
      state.student.maxStudent = payload;
    },
    studentTotalCntSet: (state, { payload }) => {
      state.student.totalCnt = payload;
    },
    studentTotalCntAdded: (state) => {
      state.student.totalCnt += 1;
    },
    studentTotalCntRemoved: (state) => {
      state.student.totalCnt -= 1;
    },
    studentAddCntChanged: (state, { payload }) => {
      state.student.addCnt = payload;
    },
    studentAddCntRemoved: (state) => {
      state.student.addCnt += 1;
    },
    studentListChanged: (state, { payload }) => {
      state.student.list = payload;
    },
    studentListRemoved: (state, { payload }) => {
      state.student.list = state.student.list.filter(
        ({ seq }) => seq !== payload
      );
    },
    studentListAdded: (state, { payload }) => {
      state.student.list = [...payload, ...state.student.list];
    },
    studentPageChanged: (state, { payload }) => {
      state.student.page = payload;
    },
    studentKeywordChanged: (state, { payload }) => {
      state.student.keyword = payload;
    },
    studentGroupSeqListChanged: (state, { payload }) => {
      const { seq, groupSeqList } = payload;
      state.student.list = state.student.list.reduce(
        (acc: Student[], cur) =>
          cur.seq === seq
            ? [...acc, { ...cur, ...{ groupSeqList } }]
            : [...acc, cur],
        []
      );
    },
    studentGroupListChanged: (state, { payload }) => {
      state.student.groupList = payload;
    },
    groupTotalCntSet: (state, { payload }) => {
      state.group.totalCnt = payload;
    },
    groupTotalCntAdded: (state) => {
      state.group.totalCnt += 1;
    },
    groupTotalCntRemove: (state) => {
      state.group.totalCnt -= 1;
    },
    groupListChanged: (state, { payload }) => {
      if (payload.seq) {
        state.group.list = state.group.list.map((item) =>
          item.seq === payload.seq ? payload.entity : item
        );
      } else {
        state.group.list = payload;
      }
    },
    groupListRemoved: (state, { payload }) => {
      state.group.list = state.group.list.filter(({ seq }) => seq !== payload);
    },
    groupListAdded: (state, { payload }) => {
      state.group.list = [payload, ...state.group.list];
    },
    groupMemberRemoved: (state, { payload }) => {
      const { groupSeq, memberSeq } = payload;
      const removedList = state.group.list.map((item) => {
        if (item.seq === groupSeq) {
          item.studentList = item.studentList.filter(
            (student) => student.seq !== memberSeq
          );
          item.cnt = item.cnt - 1;
        }
        return item;
      });

      state.group.list = removedList;
    },
    groupPageChanged: (state, { payload }) => {
      state.group.page = payload;
    },
    groupKeywordChanged: (state, { payload }) => {
      state.group.keyword = payload;
    },
    teacherTotalCntSet: (state, { payload }) => {
      state.teacher.totalCnt = payload;
    },
    teacherTotalCntAdded: (state) => {
      state.teacher.totalCnt += 1;
    },
    teacherTotalCntRemoved: (state) => {
      state.teacher.totalCnt -= 1;
    },
    teacherListChanged: (state, { payload }) => {
      state.teacher.list = payload;
    },
    teacherListRemoved: (state, { payload }) => {
      state.teacher.list = state.teacher.list.filter(
        ({ seq }) => seq !== payload
      );
    },
    teacherKeywordChanged: (state, { payload }) => {
      state.teacher.keyword = payload;
    },
    teacherPageChanged: (state, { payload }) => {
      state.teacher.page = payload;
    },
  },
});

export const {
  classInfoTypeChanged,
  studentCountChanged,
  studentMaxStudentChanged,
  studentTotalCntSet,
  studentTotalCntAdded,
  studentTotalCntRemoved,
  studentAddCntChanged,
  studentAddCntRemoved,
  studentListChanged,
  studentListRemoved,
  studentListAdded,
  studentPageChanged,
  studentKeywordChanged,
  studentGroupSeqListChanged,
  studentGroupListChanged,
  groupTotalCntSet,
  groupTotalCntAdded,
  groupTotalCntRemove,
  groupListChanged,
  groupListRemoved,
  groupListAdded,
  groupMemberRemoved,
  groupPageChanged,
  groupKeywordChanged,
  teacherTotalCntSet,
  teacherTotalCntAdded,
  teacherTotalCntRemoved,
  teacherListChanged,
  teacherListRemoved,
  teacherKeywordChanged,
  teacherPageChanged,
} = classroomSlice.actions;

export const selectClassInfoType = (state: RootState) => state.classroom.type;

export const selectStudentCount = (state: RootState) =>
  state.classroom.student.countStudent;

export const selectMaxStudent = (state: RootState) =>
  state.classroom.student.maxStudent;

export const selectStudentList = (state: RootState) =>
  state.classroom.student.list;

export const selectStudentTotalCnt = (state: RootState) =>
  state.classroom.student.totalCnt;

export const selectStudentAddCnt = (state: RootState) =>
  state.classroom.student.addCnt;

export const selectStudentPage = (state: RootState) =>
  state.classroom.student.page;

export const selectStudentSearch = (state: RootState) =>
  state.classroom.student.keyword;

export const selectStudentGroupList = (state: RootState) =>
  state.classroom.student.groupList;

export const selectGroupList = (state: RootState) => state.classroom.group.list;

export const selectGroupTotalCnt = (state: RootState) =>
  state.classroom.group.totalCnt;

export const selectGroupPage = (state: RootState) => state.classroom.group.page;

export const selectGroupSearch = (state: RootState) =>
  state.classroom.group.keyword;

export const selectTeacherList = (state: RootState) =>
  state.classroom.teacher.list;

export const selectTeacherTotalCnt = (state: RootState) =>
  state.classroom.teacher.totalCnt;

export const selectTeacherSearch = (state: RootState) =>
  state.classroom.teacher.keyword;

export const selectTeacherPage = (state: RootState) =>
  state.classroom.teacher.page;

export default classroomSlice.reducer;
